import { useEffect, useRef } from "react";

const reasons = [
  {
    icon: "ri-focus-3-line",
    title: "Enfoque Estratégico",
    description: "Cada acción está alineada con tus objetivos de negocio. Nada al azar, todo con propósito y dirección clara hacia el crecimiento.",
  },
  {
    icon: "ri-user-settings-line",
    title: "Soluciones Personalizadas",
    description: "No creemos en soluciones genéricas. Cada estrategia se construye desde cero, adaptada a tu industria, audiencia y metas específicas.",
  },
  {
    icon: "ri-robot-2-line",
    title: "Inteligencia Artificial",
    description: "Utilizamos las herramientas de IA más avanzadas del mercado para optimizar campañas, analizar datos y escalar resultados a un ritmo superior.",
  },
  {
    icon: "ri-line-chart-line",
    title: "Orientación a Resultados",
    description: "Medimos cada acción con KPIs claros y transparentes. Nuestro éxito está directamente vinculado al tuyo.",
  },
  {
    icon: "ri-pie-chart-2-line",
    title: "Análisis de Datos",
    description: "Tomamos decisiones basadas en datos reales, no en suposiciones. Dashboards en tiempo real para visibilidad total de tu rendimiento.",
  },
  {
    icon: "ri-seedling-line",
    title: "Crecimiento Sostenible",
    description: "Construimos bases sólidas para un crecimiento a largo plazo, no solo resultados inmediatos que se desvanecen en semanas.",
  },
];

function ReasonCard({ reason, index }: { reason: typeof reasons[0]; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            el.style.transition = "opacity 0.7s ease, transform 0.7s ease";
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
          }, index * 80);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    el.style.opacity = "0";
    el.style.transform = "translateY(30px)";
    observer.observe(el);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={cardRef}
      className="rounded-xl p-8 border border-[#1a1a1a] hover:border-[#C9A227]/20 transition-all duration-300 group"
      style={{ background: "rgba(255,255,255,0.025)" }}
    >
      {/* Stars */}
      <div className="flex gap-1 mb-5">
        {[...Array(5)].map((_, i) => (
          <i key={i} className="ri-star-fill text-[#C9A227] text-xs"></i>
        ))}
      </div>

      {/* Icon */}
      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#C9A227]/10 border border-[#C9A227]/20 group-hover:border-[#C9A227]/40 transition-colors duration-300 mb-4">
        <i className={`${reason.icon} text-[#C9A227] text-xl`}></i>
      </div>

      {/* Title */}
      <h3
        className="text-white text-xl font-bold mb-3"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {reason.title}
      </h3>

      {/* Description */}
      <p
        className="text-gray-500 text-sm leading-relaxed"
        style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
      >
        {reason.description}
      </p>

      {/* Footer */}
      <div className="mt-6 pt-5 border-t border-[#1a1a1a] flex items-center gap-3">
        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1a1a1a] border border-[#C9A227]/20 flex-shrink-0">
          <span className="text-[#C9A227] text-xs font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>I</span>
        </div>
        <div>
          <div className="text-white text-xs font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>Imperial Digital</div>
          <div className="text-gray-600 text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>Consultoría Premium</div>
        </div>
      </div>
    </div>
  );
}

export default function WhyUsSection() {
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.transition = "opacity 0.8s ease, transform 0.8s ease";
          el.style.opacity = "1";
          el.style.transform = "translateY(0)";
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    el.style.opacity = "0";
    el.style.transform = "translateY(20px)";
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="porque" className="py-32" style={{ background: "#0f0f0f" }}>
      <div className="max-w-7xl mx-auto px-8 lg:px-16">

        {/* Header */}
        <div ref={titleRef} className="mb-16">
          <div className="flex items-center gap-2 mb-5">
            <span className="w-2 h-2 rounded-full bg-[#C9A227] flex-shrink-0"></span>
            <span className="text-[#C9A227] text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              VENTAJAS
            </span>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 items-end">
            <div>
              <h2
                className="text-white text-4xl lg:text-5xl font-bold leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Por qué elegir{" "}
                <span className="text-[#C9A227]">Imperial Digital</span>
              </h2>
            </div>
            <div>
              <p className="text-gray-500 text-base leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                Enfoque estratégico con resultados medibles. Combinamos experiencia, tecnología y visión de negocio para llevar tu marca al siguiente nivel.
              </p>
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasons.map((reason, index) => (
            <ReasonCard key={reason.title} reason={reason} index={index} />
          ))}
        </div>

        {/* Banner */}
        <div
          className="mt-16 rounded-2xl p-10 flex flex-col lg:flex-row items-center justify-between gap-8 border border-[#C9A227]/20 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #111111, #151515)" }}
        >
          <div className="absolute inset-0 opacity-5">
            <img
              src="https://readdy.ai/api/search-image?query=abstract%20geometric%20golden%20lines%20pattern%20dark%20background%20luxury%20premium%20minimal%20corporate%20texture%20overlay&width=1200&height=200&seq=banner-bg-1&orientation=landscape"
              alt="banner background"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="relative z-10">
            <h3
              className="text-white text-2xl lg:text-3xl font-bold mb-2"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              ¿Listo para transformar tu marca?
            </h3>
            <p className="text-gray-400 text-sm" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              Nuestro equipo de expertos está disponible para analizar tu caso sin compromiso.
            </p>
          </div>
          <button
            onClick={() => {
              const el = document.getElementById("contacto");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="relative z-10 bg-[#C9A227] hover:bg-[#b8911e] text-black font-bold px-10 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer text-sm tracking-wide flex-shrink-0"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Hablar con un experto
          </button>
        </div>
      </div>
    </section>
  );
}
